import type { CustomerOrder } from '../../customerOrdersApi'
import {
  DELIVERY_ESTIMATED_WINDOW_MS,
  ORDER_STATUS_FILTER_OPTIONS,
  type OrderDateFilterOption,
  type OrderVisualStatus,
} from './ordersListConstants'

export const parseTimestamp = (value: string | null | undefined) => {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

export const parseMoneyInput = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const normalized = Number(trimmed.replace(',', '.'))
  return Number.isFinite(normalized) ? normalized : null
}

export const toStartOfDayTimestamp = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = new Date(`${trimmed}T00:00:00`)
  const timestamp = parsed.getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

export const toEndOfDayTimestamp = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = new Date(`${trimmed}T23:59:59.999`)
  const timestamp = parsed.getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

export const resolveVisualStatus = (order: CustomerOrder, nowTimestamp: number): OrderVisualStatus => {
  if (order.status === 'cancelled') return 'cancelled'
  if (order.status === 'shipped') {
    const shippedTimestamp = parseTimestamp(order.shippedAt)
    if (shippedTimestamp > 0 && nowTimestamp - shippedTimestamp >= DELIVERY_ESTIMATED_WINDOW_MS) {
      return 'delivered'
    }
    return 'shipped'
  }
  return order.status
}

export const toVisualStatusLabel = (status: OrderVisualStatus, language: 'it' | 'en') => {
  if (language === 'en') {
    if (status === 'created') return 'Processing'
    if (status === 'paid') return 'Paid'
    if (status === 'shipped') return 'Shipped'
    if (status === 'delivered') return 'Delivered'
    return 'Cancelled'
  }

  if (status === 'created') return 'In lavorazione'
  if (status === 'paid') return 'Pagato'
  if (status === 'shipped') return 'Spedito'
  if (status === 'delivered') return 'Consegnato'
  return 'Annullato'
}

export const toVisualStatusBadgeClasses = (status: OrderVisualStatus) => {
  if (status === 'paid') {
    return 'ca-badge-success'
  }
  if (status === 'shipped') {
    return 'ca-badge-info'
  }
  if (status === 'delivered') {
    return 'ca-badge-neutral'
  }
  if (status === 'cancelled') {
    return 'ca-badge-error'
  }
  return 'ca-badge-warning'
}

export const toDatePresetThreshold = (dateFilter: OrderDateFilterOption, nowTimestamp: number) => {
  if (dateFilter === 'last-30-days') return nowTimestamp - 30 * 24 * 60 * 60 * 1000
  if (dateFilter === 'last-3-months') return nowTimestamp - 90 * 24 * 60 * 60 * 1000
  if (dateFilter === 'last-year') return nowTimestamp - 365 * 24 * 60 * 60 * 1000
  return null
}

export const resolveStatusFromQuery = (): OrderVisualStatus[] => {
  if (typeof window === 'undefined') return []

  const candidate = new URLSearchParams(window.location.search).get('status')?.trim().toLowerCase()
  if (!candidate) return []

  return ORDER_STATUS_FILTER_OPTIONS.includes(candidate as OrderVisualStatus)
    ? [candidate as OrderVisualStatus]
    : []
}

export const getStatusSortOrder = (status: OrderVisualStatus) => {
  if (status === 'created') return 0
  if (status === 'paid') return 1
  if (status === 'shipped') return 2
  if (status === 'delivered') return 3
  return 4
}

export const buildVisiblePages = (currentPage: number, totalPages: number) => {
  if (totalPages <= 1) return [1]

  const windowSize = 5
  const halfWindow = Math.floor(windowSize / 2)
  let start = Math.max(1, currentPage - halfWindow)
  const end = Math.min(totalPages, start + windowSize - 1)

  if (end - start + 1 < windowSize) {
    start = Math.max(1, end - windowSize + 1)
  }

  const pages: number[] = []
  for (let value = start; value <= end; value += 1) {
    pages.push(value)
  }
  return pages
}

export const joinChipParts = (...parts: Array<string | null>) => parts.filter(Boolean).join(' - ')
